import React, { useRef, useState } from "react";
import {
  Menu, X, ChevronDown, ArrowRight,
  Code,
  Layers, Target, Briefcase, Star,
  Share2, TrendingUp, Search, Layout,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo.jpeg";

/* ─── NAV STRUCTURE ────────────────────────────────────────── */
const navStructure = [
  {
    type: "dropdown",
    name: "Branding & Marketing",
    tagline: "Creative & performance marketing services that grow your brand.",
    accent: "from-purple-600 to-pink-500",
    iconBg: "bg-purple-500/15 border-purple-500/25",
    iconColor: "text-purple-400",
    ctaPath: "/branding",
    ctaLabel: "Branding Hub",
    items: [
      { icon: <Star className="w-5 h-5" />, name: "Branding Overview", path: "/branding", desc: "Full branding & marketing hub" },
      { icon: <Share2 className="w-5 h-5" />, name: "Social Media Marketing", path: "/social-media", desc: "Build & grow your social presence" },
      { icon: <TrendingUp className="w-5 h-5" />, name: "Digital Marketing", path: "/digital-marketing", desc: "PPC, email & performance marketing" },
      { icon: <Search className="w-5 h-5" />, name: "SEO & Content Strategy", path: "/seo-content", desc: "Rank higher, reach wider" },
      { icon: <Layout className="w-5 h-5" />, name: "UI/UX & Brand Identity", path: "/uiux-branding", desc: "Designs that convert & delight" },
    ],
  },
  {
    type: "dropdown",
    name: "Software Development",
    tagline: "End-to-end software engineering — web, mobile, cloud & enterprise.",
    accent: "from-orange-500 to-amber-400",
    iconBg: "bg-orange-500/15 border-orange-500/25",
    iconColor: "text-orange-400",
    ctaPath: "/software-development",
    ctaLabel: "Software Hub",
    items: [
      { icon: <Star className="w-5 h-5" />, name: "Software Development", path: "/software-development", desc: "Full software development hub" },
      { icon: <Code className="w-5 h-5" />, name: "Core Solutions", path: "/software-solutions", desc: "Web, mobile & enterprise apps" },
      { icon: <Target className="w-5 h-5" />, name: "Strategy & Architecture", path: "/strategy-and-architecture", desc: "Software strategy & execution blueprint" },
      { icon: <Layers className="w-5 h-5" />, name: "Tech Stack & Tooling", path: "/tech-stack", desc: "Our full technology toolkit" },
      { icon: <Briefcase className="w-5 h-5" />, name: "Case Studies", path: "/case-studies", desc: "Our team & technical expertise" },
    ],
  },
  { type: "link", name: "About Us", path: "/about-us" },
];

/* ─── ROUTE → THEME MAPS ────────────────────────────────────── */
const BRANDING_ROUTES = ["/branding", "/social-media", "/digital-marketing", "/seo-content", "/uiux-branding"];

/* ─── MEGA MENU ─────────────────────────────────────────────── */
const MegaMenu = ({ item, isOpen, onNavigate }) => {
  const cols = item.items.length >= 6 ? "grid-cols-3" : "grid-cols-2";
  return (
    <div
      className="absolute left-0 right-0 bg-gray-950 border-t border-white/10 shadow-2xl"
      style={{
        top: "100%",
        opacity: isOpen ? 1 : 0,
        transform: isOpen ? "translateY(0)" : "translateY(-6px)",
        pointerEvents: isOpen ? "auto" : "none",
        transition: "opacity 0.22s ease, transform 0.22s ease",
        zIndex: 99,
      }}
    >
      <div className={`h-0.5 bg-gradient-to-r ${item.accent}`} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-10">
          <div className="w-52 flex-shrink-0 border-r border-white/8 pr-10 flex flex-col justify-between">
            <div>
              <p className={`text-sm font-black uppercase tracking-[0.3em] mb-3 bg-gradient-to-r ${item.accent} bg-clip-text text-transparent`}>
                {item.name}
              </p>
              <p className="text-white/50 text-sm leading-relaxed">{item.tagline}</p>
            </div>
            <button
              onClick={() => onNavigate(item.ctaPath)}
              className={`mt-6 inline-flex items-center text-sm font-black uppercase tracking-wider bg-gradient-to-r ${item.accent} bg-clip-text text-transparent group`}
            >
              {item.ctaLabel}
              <ArrowRight className="w-3.5 h-3.5 ml-1.5 text-orange-400 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>
          <div className={`flex-1 grid ${cols} gap-2`}>
            {item.items.map((sub) => (
              <button
                key={sub.name}
                onClick={() => onNavigate(sub.path)}
                className="group flex items-start space-x-4 p-4 rounded-xl hover:bg-white/5 transition-all duration-150 text-left"
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg border ${item.iconBg} flex items-center justify-center ${item.iconColor} group-hover:scale-110 transition-transform duration-200`}>
                  {sub.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-bold text-white/85 group-hover:text-white leading-snug mb-1 transition-colors duration-150">{sub.name}</p>
                  <p className="text-sm text-white/40 leading-tight group-hover:text-white/60 transition-colors duration-150">{sub.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/5" />
    </div>
  );
};

/* ─── MAIN NAVBAR ────────────────────────────────────────────── */
const PortfolioNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    setOpenAccordion(null);
    setOpenDropdown(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEnter = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(name);
  };

  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  const toggleAccordion = (name) =>
    setOpenAccordion((prev) => (prev === name ? null : name));

  /* ── Theme detection ── */
  const isBranding = BRANDING_ROUTES.includes(location.pathname);
  const isHome = location.pathname === "/";

  const navBg = scrolled
    ? isBranding ? "bg-purple-950 shadow-xl" : "bg-gray-950 shadow-xl"
    : isHome
    ? "bg-transparent"
    : isBranding
    ? "bg-purple-900 shadow-md"
    : "bg-gray-950 shadow-md border-b border-orange-500/20";

  const navStyle = !scrolled && isBranding && !isHome
    ? { backgroundColor: "rgb(88, 28, 135)" }
    : {};

  /* Theme-derived color tokens */
  const activeColor   = isBranding ? "text-pink-300"   : "text-orange-400";
  const activeBg      = isBranding ? "bg-pink-500/10"  : "bg-orange-500/10";
  const activeUnderline = isBranding ? "bg-pink-300"   : "bg-orange-400";
  const mobileHover   = isBranding ? "hover:text-pink-400" : "hover:text-orange-400";
  const ctaBtnClass   = isBranding
    ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 shadow-purple-500/20"
    : "bg-orange-500 hover:bg-orange-400 shadow-orange-500/20";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}
      style={navStyle}
      onMouseLeave={handleLeave}
    >
      {/* ── Top bar ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 text-white">

          {/* Logo */}
          <div
            className="flex items-center space-x-3 cursor-pointer group flex-shrink-0"
            onClick={() => handleNavigation("/")}
          >
            <img src={logo} alt="Winsil Evolution Logo" className="w-13 h-13 rounded-xl object-cover flex-shrink-0 shadow-md" style={{ width: "52px", height: "52px" }} />
            <div className="hidden sm:block">
              <p className="text-base font-black tracking-widest leading-none">WINSIL EVOLUTION</p>
              <p className="text-[11px] tracking-wide text-white/50 leading-tight mt-0.5">Software Development & Engineering</p>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-1">
            {navStructure.map((item) =>
              item.type === "link" ? (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.path)}
                  className={`text-lg font-black uppercase tracking-wider px-3 py-2 transition-colors duration-200 relative ${
                    location.pathname === item.path ? activeColor : "text-white/80 hover:text-white"
                  }`}
                >
                  {item.name}
                  {location.pathname === item.path && (
                    <span className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full ${activeUnderline}`} />
                  )}
                </button>
              ) : (
                <button
                  key={item.name}
                  onMouseEnter={() => handleEnter(item.name)}
                  className={`flex items-center space-x-1 text-lg font-black uppercase tracking-wider px-3 py-2 transition-colors duration-200 ${
                    openDropdown === item.name ? activeColor : "text-white/80 hover:text-white"
                  }`}
                >
                  <span>{item.name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.name ? "rotate-180" : ""}`} />
                </button>
              )
            )}
          </div>

          {/* CTA + Mobile toggle */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleNavigation("/contact")}
              className={`hidden sm:inline-flex lg:inline-flex items-center px-5 py-2 text-sm font-black uppercase tracking-wider rounded-full text-white transition-all duration-300 shadow-lg ${ctaBtnClass}`}
            >
              Get in Touch
            </button>
            <button
              className={`lg:hidden p-2 text-white/80 rounded transition-colors ${mobileHover}`}
              onClick={() => { setIsMenuOpen(!isMenuOpen); setOpenAccordion(null); }}
              aria-label="Toggle Menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Desktop Mega Menus ── */}
      {navStructure.filter((n) => n.type === "dropdown").map((item) => (
        <div key={item.name} onMouseEnter={() => handleEnter(item.name)}>
          <MegaMenu item={item} isOpen={openDropdown === item.name} onNavigate={handleNavigation} />
        </div>
      ))}

      {/* ── Mobile Menu ── */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out bg-gray-950 border-t border-white/10 ${
          isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 py-2 pb-4">
          {navStructure.map((item) =>
            item.type === "link" ? (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.path)}
                className={`w-full text-left block text-base font-bold py-3.5 px-2 border-b border-white/10 uppercase tracking-wider ${
                  location.pathname === item.path ? activeColor : `text-white/80 ${mobileHover}`
                }`}
              >
                {item.name}
              </button>
            ) : (
              <div key={item.name} className="border-b border-white/10">
                <button
                  onClick={() => toggleAccordion(item.name)}
                  className={`w-full flex items-center justify-between py-3.5 px-2 text-base font-black uppercase tracking-wider text-white/80 transition-colors ${mobileHover}`}
                >
                  <span>{item.name}</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      openAccordion === item.name ? `rotate-180 ${activeColor}` : ""
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openAccordion === item.name ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="pb-3 pl-2 space-y-1">
                    {item.items.map((sub) => (
                      <button
                        key={sub.name}
                        onClick={() => handleNavigation(sub.path)}
                        className={`w-full text-left flex items-start space-x-3 py-2.5 px-3 rounded-xl transition-colors duration-150 ${
                          location.pathname === sub.path
                            ? `${activeBg} ${activeColor}`
                            : "hover:bg-white/5 text-white/65 hover:text-white"
                        }`}
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg border ${item.iconBg} flex items-center justify-center ${item.iconColor}`}>
                          {React.cloneElement(sub.icon, { className: "w-4 h-4" })}
                        </div>
                        <div>
                          <p className="text-base font-bold leading-snug">{sub.name}</p>
                          <p className="text-xs text-white/40 mt-0.5">{sub.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )
          )}
          <div className="pt-4">
            <button
              onClick={() => handleNavigation("/contact")}
              className={`w-full py-3 text-sm font-black uppercase tracking-wider rounded-lg text-white transition-colors duration-300 ${ctaBtnClass}`}
            >
              Get in Touch
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PortfolioNavbar;
